import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { resolveImageUrl } from '../utils/urlResolver';
import BackButton from '../components/BackButton';
import { LayoutDashboard, Home } from 'lucide-react';

const WaysOfSeeingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  useEffect(() => {
    loadArtwork();
  }, [location.state, searchParams]);

  useEffect(() => {
    if (!loading) setHasLoadedOnce(true);
  }, [loading]);

  const loadArtwork = async () => {
    try {
      setLoading(true);
      console.log('[WaysOfSeeingPage] Loading artwork from location.state:', location.state);
      console.log('[WaysOfSeeingPage] Search params:', searchParams.get('artworkId'));
      
      // Try to get artwork from location.state first
      let selectedArtwork = location.state?.artwork;
      
      if (selectedArtwork) {
        console.log('[WaysOfSeeingPage] Found artwork in location.state:', selectedArtwork);
        setArtwork(selectedArtwork);
        setLoading(false);
        return;
      }
      
      // If not in state, try to fetch from query param
      const artworkId = searchParams.get('artworkId');
      if (artworkId && user) {
        console.log('[WaysOfSeeingPage] Fetching artwork from Firestore, ID:', artworkId);
        // Try artworks collection first
        try {
          const artworkDoc = await getDoc(doc(db, 'artworks', artworkId));
          if (artworkDoc.exists()) {
            selectedArtwork = { id: artworkDoc.id, ...artworkDoc.data() };
            console.log('[WaysOfSeeingPage] Found in artworks collection:', selectedArtwork);
          }
        } catch (e) {
          console.log('[WaysOfSeeingPage] Not in artworks collection, trying sharedArtworks...');
        }
        
        // Try sharedArtworks collection if not found
        if (!selectedArtwork) {
          try {
            const sharedDoc = await getDoc(doc(db, 'sharedArtworks', artworkId));
            if (sharedDoc.exists()) {
              const data = sharedDoc.data();
              selectedArtwork = {
                id: sharedDoc.id,
                title: data.artworkTitle || data.title,
                description: data.artworkDescription || data.description,
                imageUrl: data.artworkUrl || data.imageUrl,
                ...data
              };
              console.log('[WaysOfSeeingPage] Found in sharedArtworks collection:', selectedArtwork);
            }
          } catch (e) {
            console.error('[WaysOfSeeingPage] Error fetching artwork:', e);
          }
        }
      }
      
      if (selectedArtwork) {
        console.log('[WaysOfSeeingPage] Setting artwork:', selectedArtwork);
      } else {
        console.warn('[WaysOfSeeingPage] No artwork found');
      }
      
      setArtwork(selectedArtwork);
    } catch (error) {
      console.error('[WaysOfSeeingPage] Error loading artwork:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !hasLoadedOnce) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-amber-100 flex items-center justify-center">
        <div className="text-[#1F2937] text-lg">Loading...</div>
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-amber-100 p-6">
        <div className="max-w-4xl mx-auto">
          <BackButton onClick={() => navigate('/my-art-world')} />
          <div className="text-center py-12">
            <p className="text-[#1F2937] text-lg mb-4">No artwork selected</p>
            <button
              onClick={() => navigate('/my-art-world')}
              className="px-6 py-3 rounded-[20px] bg-gradient-to-r from-[#F7E6A1] via-[#FFE7B3] to-[#FFD7C8] text-[#1F2937] font-semibold"
            >
              Go Back to My Art World
            </button>
          </div>
        </div>
      </div>
    );
  }

  const imageUrl = resolveImageUrl(artwork.imageUrl || artwork.artworkUrl);

  return (
    <div className="min-h-screen flex flex-col bg-[#FFF8E1] px-0 py-0 md:px-[2.5cm] md:py-6">
      {loading && hasLoadedOnce && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-30 bg-white/90 border border-amber-200 text-amber-800 text-xs px-3 py-1.5 rounded-full shadow">
          Refreshing artwork...
        </div>
      )}
      <div className="relative z-10 w-full max-w-full flex-1 flex flex-col py-0 md:py-4">
        <div className="bg-white/95 rounded-none md:rounded-[32px] shadow-2xl border-0 md:border-2 border-amber-300 flex flex-col flex-1 min-h-0 overflow-hidden">
          <header className="flex-shrink-0 bg-white/95 backdrop-blur-sm border-b border-amber-200/50 shadow-sm max-h-[72px] md:max-h-[80px]">
            <div className="flex items-center justify-between py-2 md:py-2.5 h-full min-h-[52px] px-3 md:px-6">
              <p className="text-sm md:text-base font-semibold text-gray-800 truncate pr-2">Ways of Seeing</p>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button type="button" onClick={() => navigate('/my-art-world')} className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all flex items-center justify-center" title="Artist Corner">
                  <LayoutDashboard className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                  <span className="hidden md:inline ml-1">Artist Corner</span>
                </button>
                <button type="button" onClick={() => navigate('/home')} className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all flex items-center justify-center" title="Home">
                  <Home className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                  <span className="hidden md:inline ml-1">Home</span>
                </button>
              </div>
            </div>
          </header>
          <div className="flex-1 min-h-0 overflow-auto max-w-6xl mx-auto w-full p-3 md:p-6">
        <p className="text-sm text-[#4B5563] mb-6">Explore your artwork through different artistic perspectives</p>

        {/* Selected Artwork Preview */}
        <div className="mb-8 bg-white/80 rounded-xl md:rounded-[32px] p-4 md:p-6 shadow-lg border-2 border-amber-200/50">
          <h2 className="text-xl font-semibold text-[#1F2937] mb-4">Selected Artwork</h2>
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-shrink-0">
              <img
                src={imageUrl}
                alt={artwork.title || 'Artwork'}
                className="w-full max-w-md rounded-[24px] shadow-md object-cover"
                style={{ maxHeight: '400px' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-[#1F2937] mb-2">
                {artwork.title || 'Untitled Artwork'}
              </h3>
              {artwork.description && (
                <p className="text-[#4B5563] mb-4">{artwork.description}</p>
              )}
              <p className="text-sm text-[#4B5563]">
                Ready to explore different ways of seeing this artwork!
              </p>
            </div>
          </div>
        </div>

        {/* Feature Content Placeholder */}
        <div className="bg-white/80 rounded-xl md:rounded-[32px] p-6 md:p-8 shadow-lg border-2 border-amber-200/50">
          <p className="text-[#4B5563] text-center">
            Ways of Seeing feature coming soon. This will allow you to transform your artwork into different artistic styles.
          </p>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaysOfSeeingPage;
